import React, { useState, useEffect } from 'react';
import { Brain, Power, PowerOff, Activity, CheckCircle, XCircle, Clock, Zap, TrendingUp, Cpu, Settings } from 'lucide-react';
import apiClient from '../../services/api/client';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useI18n } from '../../i18n';

// AI decision type labels
const DECISION_TYPE_LABELS = {
  'vulnerability_analysis': 'Vulnerability analysis',
  'exploit_recommendation': 'Recommandation d\'exploit',
  'exploit_selection': 'Exploit selection',
  'attack_strategy': 'Attack strategy',
  'risk_assessment': 'Risk assessment',
  'post_exploitation': 'Post-exploitation',
  'report_generation': 'Report generation',
  'remediation_advice': 'Remediation advice',
  'tool_selection': 'Tool selection',
  'ai_analysis': 'Analyse IA',
  'subdomain_discovery': 'Subdomain discovery',
  'business_context': 'Business context',
  'scan_completed': 'Scan completed',
  'pentest_report': 'Rapport de pentest'
};

// Decision status labels
const DECISION_STATUS_LABELS = {
  'applied': 'Applied',
  'pending': 'En attente',
  'rejected': 'Rejected',
  'failed': 'Failed'
};

// Model status labels
const MODEL_STATUS_LABELS = {
  'active': 'Actif',
  'inactive': 'Inactif',
  'loading': 'Chargement',
  'error': 'Erreur',
  'open': 'Ouvert',
  'closed': 'Closed',
  'ready': 'Ready',
  'offline': 'Hors ligne',
  'online': 'En ligne'
};

const OrchestratorMonitoring = ({ embedded = false }) => {
  if (!embedded) usePageTitle('Orchestrateur IA - Monitoring');
  const { t } = useI18n();
  const [status, setStatus] = useState({
    enabled: false,
    mode: 'safe',
    active_scans: 0,
    total_decisions: 0,
    ai_models_loaded: 0,
    confidence_threshold: 0.7
  });
  const [decisions, setDecisions] = useState([]);
  const [aiModels, setAiModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      setError(null);

      // Fetch real data from API - handle errors gracefully
      const [statusResponse, decisionsResponse, llmsResponse] = await Promise.all([
        apiClient.get('/orchestrator/status').catch(err => {
          if (err.response?.status === 401) return null;
          if (err.response?.status === 500 || err.response?.status === 503) {
            console.warn(`[Orchestrator] Service temporairement indisponible (${err.response?.status})`);
            return { data: null, serverError: true };
          }
          throw err;
        }),
        apiClient.get('/orchestrator/decisions?limit=20').catch(err => {
          if (err.response?.status === 401) return null;
          if (err.response?.status === 500 || err.response?.status === 503) {
            console.warn(`[Orchestrator] Decisions temporarily unavailable (${err.response?.status})`);
            return { data: null, serverError: true };
          }
          throw err;
        }),
        apiClient.get('/orchestrator/available-llms').catch(() => null),
      ]);

      // Handle case where user was redirected to login (401)
      if (!statusResponse && !decisionsResponse) {
        return;
      }

      // Check if any response had a server error (500) - show friendly message instead of error
      const hasServerError = statusResponse?.serverError || decisionsResponse?.serverError;

      if (statusResponse?.data) {
        const stats = statusResponse.data.stats || {};
        setStatus({
          enabled: stats.enabled || statusResponse.data.auto_exploitation_enabled || false,
          mode: stats.mode || statusResponse.data.exploitation_mode || 'safe',
          active_scans: stats.active_scans || 0,
          total_decisions: stats.total_decisions || 0,
          ai_models_loaded: stats.ai_models_loaded || 3,
          confidence_threshold: stats.confidence_threshold || 0.7,
          auto_exploit_enabled: stats.auto_exploit_enabled || false
        });
      } else if (hasServerError) {
        // Keep default status values on server error - component will still render
        setStatus(prev => ({
          ...prev,
          enabled: false,
          mode: 'safe',
          active_scans: 0,
          total_decisions: 0
        }));
      }

      if (decisionsResponse?.data) {
        const decisionsData = decisionsResponse.data.decisions || [];
        setDecisions(decisionsData.map(d => ({
          id: d.id,
          timestamp: d.decided_at || new Date().toISOString(),
          scan_id: d.vulnerability_id ? `VULN-${d.vulnerability_id.substring(0, 8)}` : 'N/A',
          decision_type: d.action || 'unknown',
          ai_model: 'IA',
          confidence: d.confidence || 0,
          recommendation: d.reasoning || '',
          status: d.status || 'pending'
        })));
      } else if (hasServerError) {
        setDecisions([]);
      }

      // Load real Ollama models
      if (llmsResponse?.data?.llms?.length > 0) {
        const totalModels = llmsResponse.data.llms.length;
        setAiModels(llmsResponse.data.llms.map((m, i) => ({
          name: m.name,
          id: m.id,
          status: 'active',
          usage: totalModels > 0 ? Math.round(100 / totalModels) : 0,
          description: m.description || '',
          size_gb: m.size_gb,
          lastUsed: new Date()
        })));
        setStatus(prev => ({ ...prev, ai_models_loaded: llmsResponse.data.llms.length }));
      }

      // Don't show error state for 500/503 errors - show graceful degradation instead
    } catch (err) {
      // Silently handle 401 - user will be redirected to login by API client
      if (err.response?.status === 401) return;
      // Handle 500/503 errors gracefully - already caught above, but just in case
      if (err.response?.status === 500 || err.response?.status === 503) return;

      console.error('Error retrieving orchestrator data:', err);
      setError(t('orchestrator.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  const toggleOrchestrator = async (enable) => {
    try {
      if (enable) {
        await apiClient.post('/orchestrator/enable', {
          mode: status.mode || 'safe',
          confidence_threshold: status.confidence_threshold || 0.7
        });
      } else {
        await apiClient.post('/orchestrator/disable');
      }

      setStatus({ ...status, enabled: enable });
      // Refresh data after toggle
      fetchData();
    } catch (err) {
      // Silently handle 401 - user will be redirected to login by API client
      if (err.response?.status === 401) return;
      // Handle 500/503 errors gracefully - show user-friendly message
      if (err.response?.status === 500 || err.response?.status === 503) {
        setError(t('orchestrator.serviceUnavailable'));
        return;
      }

      console.error('Error changing orchestrator state:', err);
      setError(t('orchestrator.stateChangeError'));
    }
  };

  if (loading) {
    return (
      <div className={embedded ? 'flex items-center justify-center py-12' : 'min-h-screen bg-brand-black flex items-center justify-center'}>
        <div className="w-16 h-16 border-4 border-brand-green border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={embedded ? 'py-8' : 'min-h-screen bg-brand-black p-6'}>
        <div className="flex flex-col items-center justify-center py-12">
          <Brain className="w-16 h-16 text-gray-600 mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">{t('orchestrator.title')}</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => { setError(null); setLoading(true); fetchData(); }}
            className="btn-green flex items-center gap-2"
          >
            <Activity className="w-5 h-5" />
            {t('orchestrator.retry')}
          </button>
        </div>
      </div>
    );
  }

  const inner = (
    <div className="space-y-6">
        {/* Header — hidden when embedded (parent shows the title) */}
        {!embedded && (
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Brain className="w-10 h-10 text-brand-green" />
            <div>
              <h1 className="text-4xl font-bold text-gradient-green">{t('orchestrator.title')}</h1>
              <p className="text-gray-300 mt-2 text-sm sm:text-base">{t('orchestrator.subtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              status?.enabled
                ? 'bg-brand-green/20 text-brand-green border border-brand-green/50'
                : 'bg-gray-800 text-gray-400 border border-gray-700'
            }`}>
              <div className={`w-3 h-3 rounded-full ${
                status?.enabled ? 'bg-brand-green animate-pulse-green' : 'bg-gray-600'
              }`}></div>
              <span className="font-medium">
                {status?.enabled ? t('orchestrator.active') : t('orchestrator.inactive')}
              </span>
            </div>
            {status?.enabled ? (
              <button
                onClick={() => toggleOrchestrator(false)}
                className="px-6 py-3 bg-red-500/20 text-red-400 border border-red-500/50 rounded-lg hover:bg-red-500/30 transition-colors flex items-center gap-2"
              >
                <PowerOff className="w-5 h-5" />
                <span>{t('orchestrator.disable')}</span>
              </button>
            ) : (
              <button
                onClick={() => toggleOrchestrator(true)}
                className="btn-green flex items-center gap-2"
              >
                <Power className="w-5 h-5" />
                <span>{t('orchestrator.enable')}</span>
              </button>
            )}
          </div>
        </div>
        )}

        {/* When embedded: show status + toggle inline */}
        {embedded && (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
              status?.enabled
                ? 'bg-brand-green/20 text-brand-green border border-brand-green/50'
                : 'bg-gray-800 text-gray-400 border border-gray-700'
            }`}>
              <div className={`w-2 h-2 rounded-full ${status?.enabled ? 'bg-brand-green animate-pulse' : 'bg-gray-600'}`}></div>
              <span className="font-medium">{status?.enabled ? t('orchestrator.active') : t('orchestrator.inactive')}</span>
            </div>
            <span className="text-xs text-gray-500">{t('orchestrator.subtitle')}</span>
          </div>
          {status?.enabled ? (
            <button onClick={() => toggleOrchestrator(false)} className="px-4 py-1.5 bg-red-500/20 text-red-400 border border-red-500/50 rounded-lg hover:bg-red-500/30 transition-colors flex items-center gap-2 text-sm">
              <PowerOff className="w-4 h-4" /><span>{t('orchestrator.disable')}</span>
            </button>
          ) : (
            <button onClick={() => toggleOrchestrator(true)} className="px-4 py-1.5 bg-brand-green/20 text-brand-green border border-brand-green/50 rounded-lg hover:bg-brand-green/30 transition-colors flex items-center gap-2 text-sm">
              <Power className="w-4 h-4" /><span>{t('orchestrator.enableSafe')}</span>
            </button>
          )}
        </div>
        )}

        {/* Main content — 2-col when embedded to use full width */}
        <div className={embedded ? 'grid grid-cols-1 xl:grid-cols-2 gap-5' : 'space-y-6'}>
          {/* LEFT: Stats + Models */}
          <div className="space-y-5">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: t('orchestrator.activeScans'), value: status.active_scans, icon: <Activity className="w-5 h-5 text-brand-green" /> },
                { label: t('orchestrator.aiDecisions'), value: status.total_decisions, icon: <Brain className="w-5 h-5 text-blue-400" /> },
                { label: t('orchestrator.aiModels'), value: aiModels.length || status.ai_models_loaded, icon: <Cpu className="w-5 h-5 text-purple-400" /> },
                { label: t('orchestrator.minConfidence'), value: `${Math.round((status.confidence_threshold || 0.7) * 100)}%`, icon: <TrendingUp className="w-5 h-5 text-yellow-400" /> },
              ].map((s, i) => (
                <div key={i} className="bg-brand-black-light rounded-xl p-4 border border-brand-green/30">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-gray-400">{s.label}</div>
                    {s.icon}
                  </div>
                  <div className="text-2xl font-bold text-white">{s.value}</div>
                </div>
              ))}
            </div>

            {/* AI Models Loaded */}
            <div className="bg-brand-black-light rounded-xl p-5 border border-brand-green/30">
              <div className="flex items-center gap-2 mb-4">
                <Cpu className="w-5 h-5 text-brand-green" />
                <h2 className="text-base font-bold text-brand-green">{t('orchestrator.loadedModels')}</h2>
                <span className="ml-auto text-xs text-gray-500">{aiModels.length} model{aiModels.length !== 1 ? 's' : ''}</span>
              </div>
              {aiModels.length === 0 ? (
                <div className="text-center py-6 text-gray-500 text-sm">
                  <Cpu className="w-10 h-10 text-gray-700 mx-auto mb-2" />
                  {t('orchestrator.loadingModels')}
                </div>
              ) : (
                <div className="space-y-2">
                  {aiModels.map((model, idx) => (
                    <div key={idx} className="bg-brand-black rounded-lg p-3 border border-gray-800 flex items-center gap-3 min-w-0">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-white truncate" title={model.name}>{model.name}</span>
                          <span className={`px-1.5 py-0.5 rounded text-xs flex-shrink-0 ${
                            model.status === 'active' ? 'bg-brand-green/20 text-brand-green' : 'bg-gray-700 text-gray-400'
                          }`}>
                            {MODEL_STATUS_LABELS[model.status] || model.status}
                          </span>
                        </div>
                        {model.description && <div className="text-xs text-gray-500 truncate">{model.description}</div>}
                      </div>
                      {model.size_gb && <span className="text-xs text-gray-600 flex-shrink-0">{model.size_gb} GB</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: Configuration + Recent Decisions */}
          <div className="space-y-5">
            {/* Configuration */}
            <div className="bg-brand-black-light rounded-xl p-5 border border-brand-green/30">
              <div className="flex items-center gap-2 mb-4">
                <Settings className="w-5 h-5 text-brand-green" />
                <h2 className="text-base font-bold text-brand-green">{t('orchestrator.configuration')}</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">{t('orchestrator.exploitationMode')}</label>
                  <select
                    value={status.mode}
                    onChange={(e) => setStatus({...status, mode: e.target.value})}
                    className="input-field w-full"
                  >
                    <option value="safe">Safe (Recommended)</option>
                    <option value="aggressive">Agressif</option>
                    <option value="stealth">Furtif</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">{t('orchestrator.safeModeNote')}</p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-400">{t('orchestrator.confidenceThreshold')}</label>
                    <span className="text-sm text-brand-green font-bold">{Math.round((status.confidence_threshold || 0.7) * 100)}%</span>
                  </div>
                  <input
                    type="range" min="50" max="95"
                    value={(status.confidence_threshold || 0.7) * 100}
                    onChange={(e) => setStatus({...status, confidence_threshold: e.target.value / 100})}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>50%</span><span>95%</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{t('orchestrator.minConfidenceNote')}</p>
                </div>
              </div>
            </div>

            {/* Recent AI Decisions */}
            <div className="bg-brand-black-light rounded-xl p-5 border border-brand-green/30">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-5 h-5 text-brand-green" />
                <h2 className="text-base font-bold text-brand-green">{t('orchestrator.recentDecisions')}</h2>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {decisions.map((decision) => {
                  const actionKey = (decision.decision_type || decision.action || '').toLowerCase();
                  const isScan = actionKey === 'scan_completed';
                  const isAI = actionKey.includes('analysis') || actionKey === 'ai_analysis' || actionKey === 'business_context';
                  return (
                    <div key={decision.id} className="bg-brand-black rounded-lg p-3 border border-gray-800">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                          decision.status === 'applied' || isScan ? 'bg-brand-green/20 text-brand-green' :
                          decision.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          isAI ? 'bg-blue-500/20 text-blue-400' :
                          'bg-gray-700 text-gray-400'
                        }`}>
                          {isScan ? 'Completed' : isAI ? 'AI' : (DECISION_STATUS_LABELS[decision.status] || decision.status)}
                        </span>
                        {decision.confidence > 0 && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-brand-green/10 text-brand-green">
                            {Math.round(decision.confidence * 100)}%
                          </span>
                        )}
                        {decision.decided_at && (
                          <span className="text-xs text-gray-600 ml-auto">
                            {new Date(decision.decided_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-white mb-1">
                        {DECISION_TYPE_LABELS[actionKey] || actionKey.replace(/_/g, ' ') || 'Analyse IA'}
                      </div>
                      {decision.recommendation && (
                        <div className="text-xs text-gray-500 truncate">{decision.recommendation}</div>
                      )}
                    </div>
                  );
                })}
                {decisions.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Brain className="w-10 h-10 text-gray-700 mx-auto mb-2" />
                    <p className="text-sm">{t('orchestrator.noAiActivity')}</p>
                    <p className="text-xs mt-1">{t('orchestrator.decisionsAppearHere')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
    </div>
  );

  if (embedded) return inner;

  return (
    <div className="min-h-screen bg-brand-black p-6">
      <div className="page-container">
        <div className="w-[95%] sm:w-[92%] lg:w-[88%] laptop:w-[85%] 2xl:w-[80%] max-w-[1600px] laptop:max-w-[1700px] 2xl:max-w-[1800px] mx-auto">
          {inner}
        </div>
      </div>
    </div>
  );
};

export default OrchestratorMonitoring;
